// Стартовые данные витрины: аутентификация по Telegram initData и загрузка магазина
// (docs/07-miniapp-ux.md §3). Дальше все запросы идут с Bearer JWT из tokenStore.
import { queryOptions, useQuery } from "@tanstack/react-query";
import type { UseQueryResult } from "@tanstack/react-query";

import { getInitData } from "../lib/telegram";
import { authenticateShop } from "./auth";
import { configureReauth } from "./client";
import { getShop } from "./shop";
import type { Shop } from "./shop";
import { setToken } from "./tokenStore";

/** NoTelegramError — приложение открыто не внутри Telegram, initData недоступен. */
export class NoTelegramError extends Error {
  constructor() {
    super("Приложение открыто не через Telegram");
    this.name = "NoTelegramError";
  }
}

export interface ShopBootstrap {
  shop: Shop;
  customerFirstName: string;
}

export function shopBootstrapQueryKey(slug: string) {
  return ["shop-bootstrap", slug] as const;
}

async function loadShopBootstrap(slug: string, signal?: AbortSignal): Promise<ShopBootstrap> {
  const initData = getInitData();
  if (!initData) {
    throw new NoTelegramError();
  }

  const auth = await authenticateShop(slug, initData, signal);
  setToken(auth.token);
  // Просроченный токен (401) фронт молча обменивает на новый повторным вызовом /auth
  // (docs/07-miniapp-ux.md §3) — client.ts дёргает эту функцию через configureReauth.
  configureReauth(async () => {
    const refreshed = await authenticateShop(slug, getInitData());
    setToken(refreshed.token);
  });

  const shop = await getShop(signal);
  return { shop, customerFirstName: auth.customer.first_name };
}

export function shopBootstrapQueryOptions(slug: string) {
  return queryOptions({
    queryKey: shopBootstrapQueryKey(slug),
    queryFn: ({ signal }) => loadShopBootstrap(slug, signal),
    retry: false,
  });
}

/** useShopBootstrap аутентифицирует покупателя и загружает магазин по slug из URL. */
export function useShopBootstrap(slug: string): UseQueryResult<ShopBootstrap, Error> {
  return useQuery(shopBootstrapQueryOptions(slug));
}
